import React from 'react';
import { Link } from 'react-router-dom';
import { getConfig } from '@/utils/runtimeConfig';
import { useAppConfig } from '@/hooks/useAppConfig';
import { 
  Building2, Palette, Key, Mail, Phone, MapPin, Facebook, Instagram, 
  Linkedin, Twitter, MessageCircle, ShoppingCart, Tag, Landmark, Megaphone
} from 'lucide-react';

const Footer = () => {
  const cfg = getConfig();
  const appConfig = useAppConfig();
  const version = cfg.app_version || '';
  const env = cfg.environment || '';

  return (
    <footer className="bg-black text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-5 gap-8 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="/logo-zenstudios.svg" 
                alt="ZenStudios Logo" 
                className="w-8 h-8"
              />
              <div className="text-2xl font-bold">
                <span className="text-white">Zen</span>
                <span className="text-primary">Studios</span>
                <sup className="text-xs">™</sup>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Where calm meets precision. Premium real estate, interior design, and property management services.
            </p>
            <div className="flex gap-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#4ADE80] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#4ADE80] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://wa.me/918790460259" target="_blank" rel="noopener noreferrer" className="hover:text-[#4ADE80] transition-colors">
                <MessageCircle className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#4ADE80] transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#4ADE80] transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Buy & Sell */}
          <div>
            <h3 className="font-bold text-lg mb-4">Buy & Sell</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/buy/properties" className="hover:text-[#4ADE80] transition-colors flex items-center"><ShoppingCart className="w-4 h-4 mr-2" />Browse Properties</Link></li>
              <li><Link to="/buy/post-requirement" className="hover:text-[#4ADE80] transition-colors">Post Requirement</Link></li>
              <li><Link to="/buy/legal-opinion" className="hover:text-[#4ADE80] transition-colors">Get Legal Opinion</Link></li>
              <li><Link to="/sell/individual" className="hover:text-[#4ADE80] transition-colors flex items-center"><Tag className="w-4 h-4 mr-2" />Sell as Individual</Link></li>
              <li><Link to="/sell/agent" className="hover:text-[#4ADE80] transition-colors">Agent Portal</Link></li>
              <li><Link to="/sell/developer" className="hover:text-[#4ADE80] transition-colors">Developer Portal</Link></li>
            </ul>
          </div>

          {/* Rentals */}
          <div>
            <h3 className="font-bold text-lg mb-4">Rentals</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/rentals/corporate" className="hover:text-[#4ADE80] transition-colors flex items-center"><Building2 className="w-4 h-4 mr-2" />Corporate Rentals</Link></li>
              <li><Link to="/rentals/vacation" className="hover:text-[#4ADE80] transition-colors">Vacation Rentals</Link></li>
              <li><Link to="/rentals/find" className="hover:text-[#4ADE80] transition-colors">Find Rental Places</Link></li>
              <li><Link to="/list-property" className="hover:text-[#4ADE80] transition-colors">List for Rent</Link></li>
              <li><Link to="/rentals/zen-space" className="hover:text-[#4ADE80] transition-colors">List as Zen Space</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-bold text-lg mb-4">Services</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/interior-design" className="hover:text-[#4ADE80] transition-colors flex items-center"><Palette className="w-4 h-4 mr-2" />Interior Design</Link></li>
              <li><Link to="/property-management" className="hover:text-[#4ADE80] transition-colors flex items-center"><Key className="w-4 h-4 mr-2" />Property Management</Link></li>
              <li><Link to="/home-loans" className="hover:text-[#4ADE80] transition-colors flex items-center"><Landmark className="w-4 h-4 mr-2" />Home Loans</Link></li>
              <li><Link to="/inspiration" className="hover:text-[#4ADE80] transition-colors">Design Inspiration</Link></li>
              <li><Link to="/advertise" className="hover:text-[#4ADE80] transition-colors flex items-center"><Megaphone className="w-4 h-4 mr-2" />Advertise with Us</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg mb-4">Get in Touch</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start">
                <Mail className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <a href={`mailto:${appConfig.support_email}`} className="hover:text-[#4ADE80] transition-colors" data-testid="footer-support-email">{appConfig.support_email}</a>
              </li>
              <li className="flex items-start">
                <Phone className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <a href={`tel:${appConfig.support_phone_tel}`} className="hover:text-[#4ADE80] transition-colors" data-testid="footer-support-phone">{appConfig.support_phone}</a>
              </li>
              <li className="flex items-start">
                <MapPin className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <span>Hyderabad, Telangana, India</span>
              </li>
            </ul>
            {/* WhatsApp CTA */}
            <a 
              href={`${appConfig.whatsapp_url}?text=Hi%20ZenStudios!%20I%20would%20like%20to%20know%20more%20about%20your%20services.`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#20BD5C] transition-colors"
              data-testid="footer-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Chat on WhatsApp
            </a>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <p>© {new Date().getFullYear()} ZenStudios<sup className="text-xs">™</sup>. All rights reserved.{version && <span className="ml-2 text-gray-600" data-testid="footer-version">v{version}{env && env !== 'production' ? ` · ${env.toUpperCase()}` : ''}</span>}</p>
            <div className="flex gap-6">
              <Link to="/privacy-policy" className="hover:text-[#4ADE80] transition-colors">Privacy Policy</Link>
              <Link to="/terms-conditions" className="hover:text-[#4ADE80] transition-colors">Terms & Conditions</Link>
              <Link to="/disclaimer" className="hover:text-[#4ADE80] transition-colors">Disclaimer</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;